import React, { useEffect, useState } from "react";

import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import DeleteIcon from "@mui/icons-material/Delete";
import EditIcon from "@mui/icons-material/Edit";
import TravelExploreIcon from "@mui/icons-material/TravelExplore";
import VisibilityIcon from "@mui/icons-material/Visibility";
import VisibilityOffIcon from "@mui/icons-material/VisibilityOff";
import Box from "@mui/material/Box";
import Card from "@mui/material/Card";
import CardContent from "@mui/material/CardContent";
import CardHeader from "@mui/material/CardHeader";
import IconButton from "@mui/material/IconButton";
import Tooltip from "@mui/material/Tooltip";
import Typography from "@mui/material/Typography";

import { Checkbox, Collapse, FormControl, InputLabel, ListItemText, MenuItem, Select } from "@mui/material";
import { useUserAuth } from "../contexts/authContext";
import { useCuas } from "../contexts/cuasContext";
import { useMap } from "../contexts/mapContext";
import { useLocalStorage } from "../hooks/useLocalStorage";
import { GetFeature, JumpToFeature } from "../map/mapUtil";
import { ConvertMetersToFeet } from "../util";

export default function AssetCards({ map, editFeature, setEditFeature, assetCardsOpen, setAssetCardsOpen }) {
    const [shownAssets, setShownAssets] = useState([]);
    const [filters, setFilters] = useLocalStorage("assetCardFilters", []);
    const [sortBy, setSortBy] = useLocalStorage("assetCardSort", "Name");

    const { assets } = useMap();
    const { handleFailedFetch } = useUserAuth();
    const { assetMonitors, getAssetMonitor, isAssetMonitoring, startAssetMonitoring, stopAssetMonitoring } = useCuas();

    useEffect(() => {
        let shownAssets = [...assets];

        // Filters:
        if (filters.includes("Actively Monitoring") || filters.includes("Alerts Present")) {
            shownAssets = shownAssets.filter((asset) => isAssetMonitoring(asset.uuid));
        }
        if (filters.includes("Alerts Present")) {
            shownAssets = shownAssets.filter((asset) => {
                const monitor = getAssetMonitor(asset.uuid);
                return monitor.get("highAlerts") > 0 || monitor.get("mediumAlerts") > 0 || monitor.get("noRiskAlerts") > 0;
            });
        }

        // Sorts:
        if (sortBy === "Name") {
            shownAssets.sort((a, b) => a.name.localeCompare(b.name));
        } else if (sortBy === "Most Alerts") {
            shownAssets.sort((a, b) => {
                const monitorA = getAssetMonitor(a.uuid);
                const monitorB = getAssetMonitor(b.uuid);
                const assetsA = monitorA ? monitorA.get("highAlerts") + monitorA.get("mediumAlerts") + monitorA.get("noRiskAlerts") : 0;
                const assetsB = monitorB ? monitorB.get("highAlerts") + monitorB.get("mediumAlerts") + monitorB.get("noRiskAlerts") : 0;
                return assetsB - assetsA;
            });
        }

        setShownAssets(shownAssets);
    }, [assets, assetMonitors, filters, sortBy]);

    const handleDeleteClick = async (assetUUID) => {
        if (!confirm("Are you sure you want to delete this asset?")) {
            return;
        }
        await stopAssetMonitoring(map, assetUUID);
        const requestOptions = {
            method: "PUT",
            headers: { "Content-Type": "application/json" }
        };
        await fetch(`/api/assets/delete/${assetUUID}`, requestOptions)
            .then((response) => (response.ok ? response.json() : Promise.reject(response)))
            .catch((err) => handleFailedFetch(err));
    };

    return (
        <Collapse in={assetCardsOpen} orientation="horizontal" unmountOnExit sx={{ height: "100%", overflowY: "auto", overflowX: "hidden" }}>
            <Box sx={{ width: { xs: "100vw", sm: "400px" } }}>
                <Card
                    sx={{
                        m: "4px",
                        p: "4px",
                        overflow: "unset",
                        display: "flex",
                        justifyContent: "flex-start",
                        alignItems: "center",
                        gap: "1px",
                        minHeight: "60px"
                    }}
                >
                    <IconButton onClick={() => setAssetCardsOpen(false)}>
                        <ChevronRightIcon />
                    </IconButton>
                    <FormControl size="small">
                        <InputLabel shrink>Filters</InputLabel>
                        <Select
                            multiple
                            displayEmpty
                            notched
                            value={filters}
                            onChange={(e) => setFilters(e.target.value)}
                            renderValue={(filters) => (
                                <Typography variant="body1" color="text.secondary" sx={{ justifyContent: "center", alignItems: "center", fontWeight: "bold" }}>
                                    {filters.length} active filter{filters.length !== 1 && "s"}
                                </Typography>
                            )}
                            SelectDisplayProps={{ "data-testid": "filters" }}
                            sx={{ width: "160px", maxHeight: "40px" }}
                            label="Filters"
                        >
                            <MenuItem value="Actively Monitoring" sx={{ maxHeight: "50px" }}>
                                <Checkbox checked={filters.includes("Actively Monitoring")} />
                                <ListItemText primary="Actively Monitoring" />
                            </MenuItem>
                            <MenuItem value="Alerts Present" sx={{ maxHeight: "50px" }}>
                                <Checkbox checked={filters.includes("Alerts Present")} />
                                <ListItemText primary="Alerts Present" />
                            </MenuItem>
                        </Select>
                    </FormControl>
                    <FormControl sx={{ ml: "10px", mr: "5px" }} size="small">
                        <InputLabel shrink>Sort By</InputLabel>
                        <Select
                            value={sortBy}
                            notched
                            onChange={(e) => setSortBy(e.target.value)}
                            renderValue={(sortBy) => (
                                <Typography variant="body1" color="text.secondary" sx={{ justifyContent: "center", alignItems: "center", fontWeight: "bold" }}>
                                    {sortBy}
                                </Typography>
                            )}
                            SelectDisplayProps={{ "data-testid": "sorts" }}
                            sx={{ width: "160px", maxHeight: "40px" }}
                            label="Sort By"
                        >
                            <MenuItem value="Name" sx={{ maxHeight: "50px" }}>
                                <ListItemText primary="Name" />
                            </MenuItem>
                            <MenuItem value="Most Alerts" sx={{ maxHeight: "50px" }}>
                                <ListItemText primary="Most Alerts" />
                            </MenuItem>
                        </Select>
                    </FormControl>
                </Card>
                {shownAssets.map((asset) => {
                    return (
                        <Card key={asset.uuid} sx={{ m: "4px", overflow: "unset" }}>
                            <CardHeader
                                title={
                                    <Box sx={{ display: "flex", flexDirection: "row", justifyContent: "center", alignItems: "center" }}>
                                        <Typography
                                            variant="h5"
                                            sx={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", width: "190px", mr: "auto" }}
                                        >
                                            <Tooltip title={asset.name}>
                                                <span style={{ overflow: "hidden", textOverflow: "ellipsis", whitespace: "nowrap" }}>{asset.name}</span>
                                            </Tooltip>
                                        </Typography>
                                        {!editFeature || editFeature.getId() !== asset.uuid ? (
                                            <>
                                                <Tooltip title="Monitor">
                                                    <IconButton
                                                        aria-label="Monitor"
                                                        onClick={() => {
                                                            if (isAssetMonitoring(asset.uuid)) {
                                                                stopAssetMonitoring(map, asset.uuid);
                                                            } else {
                                                                startAssetMonitoring(map, asset.uuid);
                                                            }
                                                        }}
                                                        id={`monitor-${asset.volumeId}`}
                                                    >
                                                        {isAssetMonitoring(asset.uuid) ? (
                                                            <VisibilityIcon color="success" id={`monitorOn-${asset.volumeId}`} />
                                                        ) : (
                                                            <VisibilityOffIcon color="error" id={`monitorOff-${asset.volumeId}`} />
                                                        )}
                                                    </IconButton>
                                                </Tooltip>
                                                <Tooltip title="View">
                                                    <IconButton
                                                        aria-label="View"
                                                        onClick={() => JumpToFeature(map, GetFeature(map, "assets", asset.uuid))}
                                                        id={`view-${asset.volumeId}`}
                                                    >
                                                        <TravelExploreIcon />
                                                    </IconButton>
                                                </Tooltip>
                                                <Tooltip title="Edit">
                                                    <IconButton
                                                        aria-label="Edit"
                                                        onClick={() => setEditFeature(GetFeature(map, "assets", asset.uuid))}
                                                        id={`edit-${asset.volumeId}`}
                                                    >
                                                        <EditIcon />
                                                    </IconButton>
                                                </Tooltip>
                                                <Tooltip title="Delete">
                                                    <IconButton
                                                        aria-label="Delete"
                                                        onClick={() => handleDeleteClick(asset.uuid)}
                                                        id={`delete-${asset.volumeId}`}
                                                    >
                                                        <DeleteIcon />
                                                    </IconButton>
                                                </Tooltip>
                                            </>
                                        ) : (
                                            <Typography color="text.secondary" sx={{ fontStyle: "italic" }} id="editing">
                                                Editing in Progress
                                            </Typography>
                                        )}
                                    </Box>
                                }
                                sx={{ pb: "4px !important" }}
                            />
                            <CardContent sx={{ display: "grid", gridTemplateColumns: "1fr", gap: 1, pt: "2px !important", pb: "14px !important" }}>
                                <Typography variant="body2" color="text.secondary" sx={{ display: "flex", justifyContent: "space-between" }}>
                                    <b>Buffer: </b>
                                    <span>{ConvertMetersToFeet(asset.buffer_m)}ft</span>
                                </Typography>
                                <Typography variant="body2" color="text.secondary" sx={{ display: "flex", justifyContent: "space-between" }}>
                                    <b>Altitude (AGL): </b>
                                    <span>{ConvertMetersToFeet(asset.buffer_altitude_agl_m)}ft</span>
                                </Typography>
                                <Typography variant="body2" color="text.secondary" sx={{ display: "flex", justifyContent: "space-between" }}>
                                    <b>Altitude (HAE): </b>
                                    <span>{ConvertMetersToFeet(asset.buffer_altitude_hae_m)}ft</span>
                                </Typography>
                                {isAssetMonitoring(asset.uuid) && (
                                    <Box sx={{ display: "flex", justifyContent: "space-between" }}>
                                        <Typography variant="body2" color="text.secondary">
                                            <b>Alerts:&nbsp;</b>
                                        </Typography>
                                        <Box sx={{ display: "flex", flexDirection: "row", gap: 2 }}>
                                            <Typography variant="body2" color="text.secondary">
                                                <span>High:&nbsp;</span>
                                                <span style={{ color: "red", fontWeight: "bolder" }}>{getAssetMonitor(asset.uuid).get("highAlerts")}</span>
                                            </Typography>
                                            <Typography variant="body2" color="text.secondary">
                                                <span>Medium:&nbsp;</span>
                                                <span style={{ color: "yellow", fontWeight: "lighter" }}>
                                                    {getAssetMonitor(asset.uuid).get("mediumAlerts")}
                                                </span>
                                            </Typography>
                                            <Typography variant="body2" color="text.secondary">
                                                <span>No Risk:&nbsp;</span>
                                                <span style={{ color: "lime" }}>{getAssetMonitor(asset.uuid).get("noRiskAlerts")}</span>
                                            </Typography>
                                        </Box>
                                    </Box>
                                )}
                            </CardContent>
                        </Card>
                    );
                })}
            </Box>
        </Collapse>
    );
}
